import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BrandDto } from '@ecom/contracts';
import { BilingualField, TextField, ToggleField, type BilingualValue } from '@/components/fields';
import { FilterBar } from '@/components/filters';
import { PlusIcon, TagIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, Table, TableScroll, Td, Th, Thumb, Tr } from '@/components/page';
import { SeoPanel, emptySeo, seoBody, type SeoDraft } from '@/components/seo-panel';
import { Alert, Button, EmptyState, Pill, TableSkeleton, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { MediaField } from '@/pages/home-layout/fields';
import { useBrandsStore } from '@/stores/brands.store';

/**
 * Brands (ADR-095): each a page of its own on the store — its name in both
 * languages, a paragraph, a logo, its search result. Changing its link leaves
 * the old one redirecting.
 */
export function BrandsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, search, loading, error, load, loadMore, open } = useBrandsStore();
  const [editing, setEditing] = useState<BrandDto | 'new' | null>(null);

  useEffect(() => {
    void load();
  }, [load, locale]);

  // `?edit=<id>` opens that brand — where SEO health's "Fix" leads (ADR-095).
  const [params, setParams] = useSearchParams();
  const wanted = params.get('edit');
  useEffect(() => {
    if (!wanted) return;
    void open(wanted).then((brand) => brand && setEditing(brand));
    setParams((current) => {
      current.delete('edit');
      return current;
    }, { replace: true });
  }, [wanted, open, setParams]);

  const add = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('brandAdd')}
    </Button>
  );

  return (
    <Page title={t('brands')} subtitle={t('brandsSubtitle')} actions={add} fill>
      <ListCard>
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('brandsSearch')} shown={items.length} />
        </ListToolbar>
        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}
        {loading && items.length === 0 ? (
          <TableSkeleton columns={4} />
        ) : items.length === 0 ? (
          <EmptyState title={t('brandsEmpty')} icon={<TagIcon />} action={add} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('brand')}</Th>
                  <Th>{t('brandProducts')}</Th>
                  <Th>{t('seoEngines')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.map((brand) => (
                  <Tr
                    key={brand.id}
                    className={cx(!brand.isActive && 'opacity-60')}
                    onClick={async () => {
                      // Opened with both languages and the logo's key, which the list does not carry.
                      const full = await open(brand.id);
                      if (full) setEditing(full);
                    }}
                  >
                    <Td>
                      <div className="flex items-center gap-3">
                        <Thumb src={mediaUrl(brand.logoUrl)} size="sm" />
                        <span className="flex flex-col">
                          <span className="font-medium text-ink">{brand.name}</span>
                          <span dir="ltr" className="text-start text-xs text-ink-muted">
                            /brands/{brand.slug}
                          </span>
                        </span>
                      </div>
                    </Td>
                    <Td data-numeric>{count(brand.productCount ?? 0, locale)}</Td>
                    <Td className="text-sm text-ink-muted">
                      {brand.seo?.noindex ? t('seoHidden') : brand.seo?.metaTitle ? t('brandSeoWritten') : t('brandSeoPattern')}
                    </Td>
                    <Td align="end">{!brand.isActive ? <Pill>{t('brandInactive')}</Pill> : null}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>
      {editing ? <BrandSheet brand={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Page>
  );
}

function BrandSheet({ brand, onClose }: { brand: BrandDto | null; onClose: () => void }) {
  const t = useT();
  const { save, saving, saveError } = useBrandsStore();
  const content = brand?.content;
  const [name, setName] = useState<BilingualValue>({ ar: content?.name.ar ?? '', en: content?.name.en ?? brand?.name ?? '' });
  const [description, setDescription] = useState<BilingualValue>({ ar: content?.description.ar ?? '', en: content?.description.en ?? '' });
  const [slug, setSlug] = useState(brand?.slug ?? '');
  const [logo, setLogo] = useState<{ key: string; url: string } | null>(
    content?.logoKey && brand?.logoUrl ? { key: content.logoKey, url: brand.logoUrl } : null,
  );
  const [isActive, setIsActive] = useState(brand?.isActive ?? true);
  const [seo, setSeo] = useState<SeoDraft>(content?.seo ?? emptySeo());
  const ready = Boolean(name.en.trim() || name.ar.trim());

  async function submit() {
    if (!ready) return;
    const en = name.en.trim() || name.ar.trim();
    const ok = await save(brand?.id ?? null, {
      translations: {
        en: { name: en, description: description.en.trim() || null },
        ar: { name: name.ar.trim() || en, description: description.ar.trim() || null },
      },
      ...(slug.trim() && slug.trim() !== brand?.slug ? { slug: slug.trim() } : {}),
      logoKey: logo?.key ?? null,
      isActive,
      seo: seoBody(seo),
    });
    if (ok) onClose();
  }

  return (
    <Sheet
      open
      size="lg"
      title={brand ? brand.name : t('brandAdd')}
      subtitle={t('brandEditHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={!ready || saving} onClick={() => void submit()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <BilingualField label={t('brandName')} value={name} onChange={setName} limit={200} />
        <BilingualField
          label={t('brandDescription')}
          hint={t('brandDescriptionHint')}
          multiline
          rows={3}
          limit={2000}
          value={description}
          onChange={setDescription}
        />
        <TextField
          label={t('brandLink')}
          hint={brand && slug.trim() && slug.trim() !== brand.slug ? t('brandLinkChanged') : t('brandLinkHint')}
          latin
          value={slug}
          onChange={(next) => setSlug(next.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
        />
        <MediaField
          kind="image"
          label={t('brandLogo')}
          hint={t('brandLogoHint')}
          value={logo}
          portrait={false}
          onChange={(next) => setLogo((next as { key: string; url: string } | null) ?? null)}
        />
        <ToggleField label={t('brandActive')} hint={t('brandActiveHint')} checked={isActive} onChange={setIsActive} />
        <SeoPanel
          kind="brand"
          path={`/brands/${slug.trim() || brand?.slug || '…'}`}
          draft={seo}
          onChange={setSeo}
          names={name}
          ownWords={description}
          values={{ count: brand?.productCount !== undefined ? String(brand.productCount) : undefined }}
          hasImage={Boolean(logo)}
        />
        {saveError ? <Alert>{saveError}</Alert> : null}
      </div>
    </Sheet>
  );
}
